// Auth Session Service that should be use on client side

import { commonServerLogger } from '@/lib/logger'

/**
 * Clear JWT token safely using API endpoint to avoid Server Component cookie modification restrictions
 *
 * This function attempts to clear JWT tokens using the /api/auth/session/jwt endpoint,
 * which works in all contexts including Server Components.
 *
 * @returns Promise<boolean> - true if token was cleared successfully, false otherwise
 */
export async function deleteAuthJwtToken() {
  // Clear JWT token using API endpoint to avoid Server Component cookie modification restrictions
  // This is necessary because Server Components cannot modify cookies directly
  try {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'
    const response = await fetch(`${baseUrl}/api/auth/session/jwt`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })

    if (!response.ok) {
      commonServerLogger('warn', 'Failed to clear JWT token via API', {
        status: response.status,
        statusText: response.statusText,
      })
      return false
    } else {
      commonServerLogger('info', 'JWT token cleared successfully via API endpoint')
      return true
    }
  } catch (error) {
    commonServerLogger('warn', 'Failed to call JWT clear API endpoint', {
      error: error instanceof Error ? error.message : 'Unknown error',
    })
    return false
  }
}

export async function deleteAuthBearerToken() {
  try {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'
    const response = await fetch(`${baseUrl}/api/auth/session/bearer`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })

    if (!response.ok) {
      commonServerLogger('warn', 'Failed to clear Bearer token via API', {
        status: response.status,
        statusText: response.statusText,
      })
      return false
    } else {
      commonServerLogger('info', 'Bearer token cleared successfully via API endpoint')
      return true
    }
  } catch (error) {
    commonServerLogger('warn', 'Failed to call Bearer clear API endpoint', {
      error: error instanceof Error ? error.message : 'Unknown error',
    })
    return false
  }
}

export async function deleteAuthSessionCookie() {
  await Promise.all([deleteAuthBearerToken(), deleteAuthJwtToken()])
}
